<template>
  <div :class="{ 'no-focus-effects': noFocusEffects }">
    <v-select
      v-model="selectedItems"
      :items="items"
      :label="label"
      :loading="loading"
      :placeholder="placeholder"
      :disabled="disabled"
      :dark="dark"
      :dense="dense"
      :flat="flat || noFocusEffects"
      multiple
      chips
      deletable-chips
      hide-selected
      persistent-hint
      :hint="hint"
      :error-messages="errorMessages"
      item-text="text"
      item-value="value"
      return-object
      @input="onSelectionChange"
      @focus="$emit('focus', $event)"
      @click="$emit('click', $event)"
    >
      <template v-slot:selection="{ item, index }">
        <v-chip
          v-if="index < 5"
          :key="item.value"
          close
          small
          @click:close="removeItem(item)"
          class="ma-1"
        >
          <span>{{ truncateText(item.text, 15) }}</span>
        </v-chip>
        <span
          v-if="index === 5"
          class="grey--text text-caption ma-1"
        >
          (+{{ selectedItems.length - 5 }} altri)
        </span>
      </template>

      <template v-slot:item="{ item, attrs, on }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          @click.stop="toggleItem(item)"
        >
          <template v-slot:prepend>
            <v-checkbox
              :input-value="isSelected(item)"
              color="primary"
              @click.stop
            ></v-checkbox>
          </template>
          <v-list-item-content>
            <v-list-item-title>
              {{ item.text }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: 'If65MultiSelect',
  props: {
    api: {
      type: Function,
      required: true
    },
    value: {
      required: true
    },
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Ricerca'
    },
    hint: {
      type: String,
      default: ''
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    extraParams: {
      type: Object,
      default: () => ({})
    },
    extraItems: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: true
    },
    flat: {
      type: Boolean,
      default: false
    },
    noFocusEffects: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      items: [],
      selectedItems: [],
      loading: false
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.updateSelectedItems(newVal);
      },
      immediate: true
    }
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      // Controllo se l'API è disponibile
      if (!this.api || typeof this.api !== 'function') {
        console.warn('API non disponibile per multiselect:', this.label);
        this.loading = false;
        return;
      }

      this.loading = true;
      const params = Object.assign({}, this.extraParams, { choices: '' });

      this.api(params)
        .then(response => {
          const items = _.cloneDeep(response.data);

          if (this.extraItems && this.extraItems.length) {
            for (const extraItem of this.extraItems) {
              items.push(extraItem);
            }
          }

          this.items = items;
          this.updateSelectedItems(this.value);
          this.$emit('items-loaded', items);
        })
        .catch(error => {
          console.error("Error loading items:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateSelectedItems(values) {
      if (!this.items.length || !values) {
        this.selectedItems = [];
        return;
      }

      if (Array.isArray(values)) {
        this.selectedItems = this.items.filter(item => values.includes(item.value));
      } else {
        this.selectedItems = this.items.filter(item => item.value === values);
      }

      // Log for debugging
      console.log('Updated selected items:', this.selectedItems);
    },
    onSelectionChange(selected) {
      // Extract values from selected items
      const values = selected.map(item => item.value);

      // Emit the values to the parent component
      this.$emit('input', values);

      // Log for debugging
      console.log('Selection changed, emitting values:', values);
    },
    isSelected(item) {
      return this.selectedItems.some(selected => selected.value === item.value);
    },
    toggleItem(item) {
      const index = this.selectedItems.findIndex(selected => selected.value === item.value);

      const newSelectedItems = [...this.selectedItems];

      if (index === -1) {
        // Add item
        newSelectedItems.push(item);
      } else {
        // Remove item
        newSelectedItems.splice(index, 1);
      }

      // Update the model
      this.selectedItems = newSelectedItems;

      // Emit the values
      this.onSelectionChange(newSelectedItems);
    },
    removeItem(item) {
      const index = this.selectedItems.findIndex(selected => selected.value === item.value);

      if (index !== -1) {
        const newSelectedItems = [...this.selectedItems];
        newSelectedItems.splice(index, 1);

        // Update the model
        this.selectedItems = newSelectedItems;

        // Emit the values
        this.onSelectionChange(newSelectedItems);
      }
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.slice(0, maxLength) + '...';
    }
  }
};
</script>

<style scoped>
.v-select__selections {
  max-width: 100%;
  overflow: visible;
  flex-wrap: wrap;
  min-height: 32px;
}

.v-chip.v-size--small {
  height: 24px;
}

.v-select.v-select--is-menu-active .v-input__slot {
  border-color: var(--v-primary-base) !important;
}


</style>
